import jsPDF from 'jspdf';
import 'jspdf-autotable';

// Fonction pour convertir un nombre en lettres
function numberToWords(num) {
    if (num === 0) return 'zéro';
    
    const units = ['', 'un', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf', 'dix', 'onze', 'douze', 'treize', 'quatorze', 'quinze', 'seize', 'dix-sept', 'dix-huit', 'dix-neuf'];
    const tens = ['', 'dix', 'vingt', 'trente', 'quarante', 'cinquante', 'soixante', 'soixante-dix', 'quatre-vingt', 'quatre-vingt-dix'];
    
    function convertHundreds(n) {
        if (n === 0) return '';
        if (n < 20) return units[n];
        if (n < 100) {
            const ten = Math.floor(n / 10);
            const unit = n % 10;
            if (unit === 0) return tens[ten];
            if (ten === 7 || ten === 9) return tens[ten] + '-' + units[unit + 10];
            return tens[ten] + '-' + units[unit];
        }
        const hundred = Math.floor(n / 100);
        const rest = n % 100;
        if (rest === 0) return (hundred === 1 ? 'cent' : units[hundred] + ' cent');
        return (hundred === 1 ? 'cent' : units[hundred] + ' cent') + ' ' + convertHundreds(rest);
    }
    
    if (num < 1000) return convertHundreds(num);
    
    const thousands = Math.floor(num / 1000);
    const remainder = num % 1000;
    const thousandText = thousands === 1 ? 'mille' : convertHundreds(thousands) + ' mille';
    if (remainder === 0) return thousandText;
    return thousandText + ' ' + convertHundreds(remainder);
}

// Fonction pour charger le logo
let logoImage = null;
let logoLoaded = false;

const loadLogo = () => {
    return new Promise((resolve) => {
        if (logoLoaded && logoImage) {
            resolve(logoImage);
            return;
        }
        const img = new Image();
        img.src = '/logo.png';
        img.onload = () => {
            logoImage = img;
            logoLoaded = true;
            resolve(img);
        };
        img.onerror = () => {
            console.warn('Logo non trouvé, utilisation du texte');
            resolve(null);
        };
    });
};

// Fonction pour enregistrer le fichier PDF
const saveAsPDF = (doc, fileName) => {
    const blob = doc.output('blob');
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    console.log(`✅ Facture enregistrée: ${fileName}`);
    
    const notification = document.createElement('div');
    notification.innerHTML = `
        <div style="position: fixed; bottom: 20px; right: 20px; background: #10b981; color: white; padding: 12px 20px; border-radius: 8px; z-index: 9999; box-shadow: 0 4px 6px rgba(0,0,0,0.1); font-family: Arial, sans-serif; font-size: 14px;">
            ✅ Facture enregistrée: ${fileName}
        </div>
    `;
    document.body.appendChild(notification);
    setTimeout(() => notification.remove(), 3000);
};

// Fonction d'impression avec choix du format
export const printInvoice = async (invoice, format = 'A4') => {
    // Définir les dimensions selon le format
    let formatConfig;
    if (format === 'A5') {
        formatConfig = { unit: 'mm', format: [148, 210] }; // A5: 148 x 210 mm
    } else {
        formatConfig = { unit: 'mm', format: 'a4' }; // A4: 210 x 297 mm
    }
    
    const doc = new jsPDF(formatConfig);
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const isA5 = format === 'A5';
    
    // Ajuster les marges et tailles pour A5
    const margin = isA5 ? 10 : 20;
    const fontSizeTitle = isA5 ? 12 : 16;
    const fontSizeNormal = isA5 ? 6 : 8;
    const fontSizeSmall = isA5 ? 5 : 7;
    const fontSizeLarge = isA5 ? 8 : 10;
    const cellPadding = isA5 ? 1.5 : 3;
    const tableFontSize = isA5 ? 6 : 7;
    
    // Couleurs
    const primaryColor = [16, 185, 129];
    const textColor = [0, 0, 0];
    const grayColor = [100, 100, 100];
    const successColor = [34, 197, 94];
    const warningColor = [245, 158, 11];
    const dangerColor = [239, 68, 68];
    
    // Charger le logo
    const logo = await loadLogo();
    
    // ============ EN-TÊTE ============
    const headerHeight = isA5 ? 35 : 48;
    doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.rect(0, 0, pageWidth, headerHeight, 'F');
    
    // Logo
    let textStartX = margin;
    if (logo) {
        try {
            const logoWidth = isA5 ? 20 : 35;
            const logoHeight = (logo.height / logo.width) * logoWidth;
            doc.addImage(logo, 'PNG', margin, isA5 ? 5 : 8, logoWidth, logoHeight);
            textStartX = margin + logoWidth + (isA5 ? 3 : 8);
        } catch (e) {
            console.warn('Erreur ajout logo:', e);
        }
    }
    
    if (!logo) {
        doc.setFontSize(fontSizeTitle);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(255, 255, 255);
        doc.text('MST', margin, isA5 ? 15 : 20);
    }
    
    // Texte de l'entreprise
    doc.setFontSize(isA5 ? 7 : 9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('MULTI SERVICE TECHNOLOGIES', textStartX, isA5 ? 12 : 18);
    
    doc.setFontSize(isA5 ? 5 : 7);
    doc.setFont('helvetica', 'normal');
    doc.text('CONSOMABLES INFORMATIQUES, ÉLECTRONIQUES', textStartX, isA5 ? 18 : 26);
    doc.text('ET ACCESSOIRES - ELECTRICITE BATIMENT', textStartX, isA5 ? 23 : 33);
    if (!isA5) {
        doc.text('ET VIDEOS SURVEILLANCE', textStartX, 40);
    }
    
    // Contact à droite
    doc.setFontSize(isA5 ? 5 : 6);
    doc.setTextColor(200, 200, 200);
    doc.text('+237 (695-295-573 / 673-960-095)', pageWidth - margin, isA5 ? 8 : 12, { align: 'right' });
    doc.text('NUI: P050017247276X', pageWidth - margin, isA5 ? 13 : 19, { align: 'right' });
    doc.text('PK13 China Mall, Douala', pageWidth - margin, isA5 ? 18 : 26, { align: 'right' });
    if (!isA5) {
        doc.text('Cameroun', pageWidth - margin, 33, { align: 'right' });
    }
    
    // Badge statut paiement
    const restAmount = invoice.total_amount - invoice.paid_amount;
    let paymentStatus = '';
    let statusColor = [];
    
    if (restAmount <= 0) {
        paymentStatus = 'PAYÉ';
        statusColor = successColor;
    } else if (invoice.paid_amount > 0) {
        paymentStatus = 'PARTIEL';
        statusColor = warningColor;
    } else {
        paymentStatus = 'EN ATTENTE';
        statusColor = dangerColor;
    }
    
    const badgeY = headerHeight + (isA5 ? 5 : 7);
    const badgeWidth = isA5 ? 30 : 40;
    const badgeHeight = isA5 ? 12 : 15;
    doc.setFillColor(statusColor[0], statusColor[1], statusColor[2]);
    doc.roundedRect(pageWidth - badgeWidth - margin, badgeY, badgeWidth, badgeHeight, 2, 2, 'F');
    doc.setFontSize(isA5 ? 7 : 9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text(paymentStatus, pageWidth - badgeWidth/2 - margin, badgeY + badgeHeight/2 + (isA5 ? 2 : 3), { align: 'center' });
    
    // ============ ZONE FACTURE DE ============
    doc.setTextColor(textColor[0], textColor[1], textColor[2]);
    doc.setFillColor(245, 245, 245);
    const factureHeight = isA5 ? 28 : 38;
    const factureY = headerHeight + (isA5 ? 2 : 5);
    doc.rect(margin, factureY, pageWidth - margin * 2 - (isA5 ? 40 : 85), factureHeight, 'F');
    
    doc.setFontSize(isA5 ? 8 : 10);
    doc.setFont('helvetica', 'bold');
    doc.text('facture de :', margin + 5, factureY + (isA5 ? 8 : 12));
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(isA5 ? 7 : 10);
    doc.text(invoice.client_name || 'Client', margin + 5, factureY + (isA5 ? 16 : 22));
    if (invoice.client_phone) {
        doc.text(invoice.client_phone, margin + 5, factureY + (isA5 ? 22 : 32));
    }
    
    // Numéro et date
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(isA5 ? 7 : 10);
    doc.text(`Facture no : ${invoice.invoice_number}`, pageWidth - margin - (isA5 ? 40 : 75), factureY + (isA5 ? 8 : 12));
    doc.setFont('helvetica', 'normal');
    const date = new Date(invoice.created_at);
    const dateStr = `${date.getDate().toString().padStart(2, '0')} ${getMonthFrench(date.getMonth())} ${date.getFullYear()}`;
    doc.text(dateStr, pageWidth - margin - (isA5 ? 40 : 75), factureY + (isA5 ? 16 : 22));
    
    doc.setFont('helvetica', 'bold');
    doc.text('DEVISE --- FCFA', pageWidth - margin - (isA5 ? 40 : 75), factureY + (isA5 ? 22 : 32));
    
    // ============ TABLEAU DES PRODUITS ============
    const tableHeaders = isA5 ? [['N°', 'DESC.', 'QTY', 'PRIX', 'TOTAL']] : [['N°', 'SKU', 'DESCRIPTION', 'QTY', 'PRIX UNIT.', 'TOTAL']];
    const tableRows = invoice.items.map((item, index) => {
        if (isA5) {
            return [
                (index + 1).toString(),
                item.product_name.length > 20 ? item.product_name.substring(0, 17) + '...' : item.product_name,
                item.quantity.toString(),
                item.unit_price.toLocaleString('fr-FR'),
                item.total.toLocaleString('fr-FR')
            ];
        } else {
            return [
                (index + 1).toString(),
                item.sku || '---',
                item.product_name.length > 35 ? item.product_name.substring(0, 32) + '...' : item.product_name,
                item.quantity.toString(),
                item.unit_price.toLocaleString('fr-FR'),
                item.total.toLocaleString('fr-FR')
            ];
        }
    });
    
    const tableStartY = factureY + factureHeight + (isA5 ? 5 : 8);
    
    doc.autoTable({
        startY: tableStartY,
        head: tableHeaders,
        body: tableRows,
        theme: 'striped',
        styles: { fontSize: tableFontSize, cellPadding: cellPadding, overflow: 'linebreak' },
        headStyles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: 'bold', halign: 'center' },
        columnStyles: isA5 ? {
            0: { cellWidth: 8, halign: 'center' },
            1: { cellWidth: 45 },
            2: { cellWidth: 12, halign: 'center' },
            3: { cellWidth: 25, halign: 'right' },
            4: { cellWidth: 25, halign: 'right' }
        } : {
            0: { cellWidth: 10, halign: 'center' },
            1: { cellWidth: 20, halign: 'center' },
            2: { cellWidth: 65 },
            3: { cellWidth: 15, halign: 'center' },
            4: { cellWidth: 30, halign: 'right' },
            5: { cellWidth: 30, halign: 'right' }
        },
        margin: { left: margin, right: margin }
    });
    
    let finalY = doc.lastAutoTable.finalY + (isA5 ? 4 : 8);
    
    // ============ GRAND TOTAL ============
    const totalWidth = isA5 ? 50 : 70;
    const totalX = pageWidth - totalWidth - margin;
    const totalTextX = totalX + (isA5 ? 3 : 5);
    const totalValueX = pageWidth - margin;
    
    doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.rect(totalX, finalY - (isA5 ? 3 : 5), totalWidth, isA5 ? 8 : 11, 'F');
    
    doc.setFontSize(isA5 ? 7 : 10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('GRAND TOTAL :', totalTextX, finalY + (isA5 ? 1 : 2));
    doc.text(`${invoice.total_amount.toLocaleString('fr-FR')} FCFA`, totalValueX, finalY + (isA5 ? 1 : 2), { align: 'right' });
    
    finalY += isA5 ? 8 : 12;
    
    // ============ MONTANT EN LETTRES ============
    doc.setFontSize(isA5 ? 5 : 8);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(grayColor[0], grayColor[1], grayColor[2]);
    const amountInWords = numberToWords(Math.floor(invoice.total_amount));
    const amountText = `La présente facture s'élève à la somme de ${amountInWords} (${invoice.total_amount.toLocaleString('fr-FR')}) francs CFA.`;
    const splitText = doc.splitTextToSize(amountText, pageWidth - margin * 2);
    doc.text(splitText, margin, finalY);
    finalY += splitText.length * (isA5 ? 3 : 5) + (isA5 ? 4 : 8);
    
    // ============ MESSAGE DE REMERCIEMENT ============
    if (!isA5 || finalY < pageHeight - 60) {
        doc.setFontSize(isA5 ? 8 : 10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
        doc.text('Merci de faire affaire avec nous !', pageWidth / 2, finalY, { align: 'center' });
        finalY += isA5 ? 6 : 10;
    }
    
    // ============ CONDITIONS GÉNÉRALES (version simplifiée pour A5) ============
    if (isA5) {
        doc.setFontSize(5);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(textColor[0], textColor[1], textColor[2]);
        doc.text('Garantie: 6 mois - Logiciel uniquement', margin, finalY);
        finalY += 5;
        doc.setFont('helvetica', 'normal');
        doc.text('PK13 China Mall, Douala, Cameroun', margin, finalY);
        finalY += 5;
    } else {
        doc.setFontSize(7);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(textColor[0], textColor[1], textColor[2]);
        doc.text('## Conditions générales :', margin, finalY);
        finalY += 5;
        
        doc.setFont('helvetica', 'normal');
        const garantieText = 'La marchandise vendue et récupérée par le client est couverte par une garantie de six (6) mois.';
        const garantieSplit = doc.splitTextToSize(garantieText, pageWidth - margin * 2);
        doc.text(garantieSplit, margin, finalY);
        finalY += garantieSplit.length * 4 + 3;
        
        doc.setFont('helvetica', 'bold');
        doc.text('NB :', margin, finalY);
        doc.setFont('helvetica', 'normal');
        const nbText1 = 'Cette garantie est strictement limitée aux défaillances logicielles.';
        const nbText2 = 'Elle n\'inclut pas les pannes ou dommages matériels (hardware).';
        const nbSplit1 = doc.splitTextToSize(nbText1, pageWidth - margin * 2 - 10);
        const nbSplit2 = doc.splitTextToSize(nbText2, pageWidth - margin * 2 - 10);
        
        doc.text(nbSplit1, margin + 10, finalY);
        finalY += nbSplit1.length * 4 + 2;
        doc.text(nbSplit2, margin + 10, finalY);
        finalY += nbSplit2.length * 4 + 10;
    }
    
    // ============ SIGNATURE ============
    if (!isA5 || finalY < pageHeight - 40) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(isA5 ? 6 : 8);
        doc.setTextColor(textColor[0], textColor[1], textColor[2]);
        doc.text('AMADJO FODA', margin, finalY);
        doc.text('AMADJO BLONDEL GÉRANT', margin, finalY + (isA5 ? 4 : 6));
        doc.text('+237( 695-295-573 / 673-960-095)', margin, finalY + (isA5 ? 8 : 12));
        doc.text('amadjoblondel@gmail.com', margin, finalY + (isA5 ? 12 : 18));
        finalY += isA5 ? 16 : 24;
    }
    
    // ============ ADRESSE BAS ============
    if (!isA5 || finalY < pageHeight - 15) {
        doc.setFontSize(isA5 ? 5 : 6);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(grayColor[0], grayColor[1], grayColor[2]);
        doc.text('PK13 china mall lieu dit après collège les LEADERS', pageWidth / 2, pageHeight - (isA5 ? 10 : 12), { align: 'center' });
    }
    
    // Générer le nom du fichier
    const clientName = (invoice.client_name || 'client').toUpperCase().replace(/[^A-Z0-9]/g, '_');
    const fileName = `${invoice.invoice_number}_${clientName}_${format}.pdf`;
    
    saveAsPDF(doc, fileName);
};

function getMonthFrench(month) {
    const months = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
    return months[month];
}